import type { DateTime } from "luxon"

import {
    MeetingAudienceFilterType,
    MeetingOrderFilterType,
} from "../../../../shared/components/filters/orderTypeFilter/helpers"
import type { DtDateRange } from "../../../../shared/components/table/StandardDateRangeFilter"
import { TimePeriodMeetingService } from "../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { ResourceType } from "../../admin/organization/resources/resourcesTypes"

import { ALL_RESOURCE_TYPES, UpcomingMeetingsPeriod } from "./upcomingMeetingsTypes"
import type { ResourceTypeFilter, UpcomingMeetingRow } from "./upcomingMeetingsTypes"

export type UpcomingMeetingsQueryParams = Readonly<{
    period?: TimePeriodMeetingService
    startDate?: string
    endDate?: string
}>

export type UpcomingMeetingsFilters = Readonly<{
    orderType: MeetingOrderFilterType
    resourceType: ResourceTypeFilter
    audience: MeetingAudienceFilterType
    search: string
}>

export type UpcomingMeetingsView = Readonly<{
    rows: UpcomingMeetingRow[]
    resourceTypeCounts: Readonly<Record<ResourceTypeFilter, number>>
}>

export type UpcomingMeetingsGroupCounts = Readonly<{
    bookings: number
    orders: number
}>

function singleDay(day: DateTime): DtDateRange {
    return { startDate: day, endDate: day }
}

// undefined = "all upcoming", no end date
export function getPeriodRange(
    period: UpcomingMeetingsPeriod,
    customRange: DtDateRange,
    now: DateTime,
): DtDateRange | undefined {
    const today = now.startOf("day")

    switch (period) {
        case UpcomingMeetingsPeriod.TODAY:
            return singleDay(today)
        case UpcomingMeetingsPeriod.TOMORROW:
            return singleDay(today.plus({ days: 1 }))
        case UpcomingMeetingsPeriod.THIS_WEEK:
            return { startDate: today, endDate: today.endOf("week").startOf("day") }
        case UpcomingMeetingsPeriod.NEXT_7_DAYS:
            return { startDate: today, endDate: today.plus({ days: 6 }) }
        case UpcomingMeetingsPeriod.NEXT_30_DAYS:
            return { startDate: today, endDate: today.plus({ days: 29 }) }
        case UpcomingMeetingsPeriod.CUSTOM:
            return customRange
        case UpcomingMeetingsPeriod.ALL:
            return undefined
    }
}

export function toUpcomingMeetingsQueryParams(
    period: UpcomingMeetingsPeriod,
    customRange: DtDateRange,
    now: DateTime,
): UpcomingMeetingsQueryParams {
    // backend periods resolve "today" server-side, so a page left open past midnight stays correct
    if (period === UpcomingMeetingsPeriod.TODAY) return { period: TimePeriodMeetingService.TODAY }
    if (period === UpcomingMeetingsPeriod.TOMORROW) return { period: TimePeriodMeetingService.TOMORROW }

    const range = getPeriodRange(period, customRange, now)
    if (!range) return { period: TimePeriodMeetingService.ALL }

    return {
        startDate: range.startDate.toISODate() ?? undefined,
        endDate: range.endDate.toISODate() ?? undefined,
    }
}

function matchesSearch(row: UpcomingMeetingRow, query: string) {
    if (!query) return true

    return [
        row.meetingDetails.meetingName,
        String(row.id),
        row.meetingDetails.location,
        row.deliveryDetails.location?.name,
    ]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(query))
}

function matchesAudience(row: UpcomingMeetingRow, audience: MeetingAudienceFilterType) {
    return audience === MeetingAudienceFilterType.ALL || row.audience === audience
}

function matchesOrderType(
    row: UpcomingMeetingRow,
    orderType: MeetingOrderFilterType,
    resourceType: ResourceTypeFilter,
) {
    if (orderType === MeetingOrderFilterType.ALL) return true
    if (row.orderKind !== orderType) return false
    if (orderType !== MeetingOrderFilterType.RESOURCE_BOOKING || resourceType === ALL_RESOURCE_TYPES) return true

    return row.resourceTypes.includes(resourceType)
}

function countResourceType(bookings: ReadonlyArray<UpcomingMeetingRow>, type: ResourceType) {
    return bookings.filter((booking) => booking.resourceTypes.includes(type)).length
}

export function toUpcomingMeetingsView(
    rows: ReadonlyArray<UpcomingMeetingRow>,
    { orderType, resourceType, audience, search }: UpcomingMeetingsFilters,
): UpcomingMeetingsView {
    // rows show the number as "#123"
    const query = search.trim().replace(/^#/, "").toLowerCase()
    const matching = rows.filter((row) => matchesAudience(row, audience) && matchesSearch(row, query))
    const bookings = matching.filter((row) => row.orderKind === MeetingOrderFilterType.RESOURCE_BOOKING)

    return {
        rows: matching.filter((row) => matchesOrderType(row, orderType, resourceType)),
        resourceTypeCounts: {
            [ALL_RESOURCE_TYPES]: bookings.length,
            [ResourceType.ROOM]: countResourceType(bookings, ResourceType.ROOM),
            [ResourceType.MOBILE_AV]: countResourceType(bookings, ResourceType.MOBILE_AV),
            [ResourceType.CAR]: countResourceType(bookings, ResourceType.CAR),
        },
    }
}

export function countGroupKinds(rows: ReadonlyArray<UpcomingMeetingRow>): UpcomingMeetingsGroupCounts {
    const bookings = rows.filter((row) => row.orderKind === MeetingOrderFilterType.RESOURCE_BOOKING).length
    return { bookings, orders: rows.length - bookings }
}

export function getLocationText(row: UpcomingMeetingRow) {
    const parts = [row.deliveryDetails.location?.name, row.meetingDetails.location]
        .map((part) => part?.trim())
        .filter((part): part is string => Boolean(part))

    return Array.from(new Set(parts)).join(" - ")
}
